package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowLeft
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.AutoFixHigh
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.widgets.GoldButton
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.posture_next_week
import evoshift.composeapp.generated.resources.posture_pick_day
import evoshift.composeapp.generated.resources.posture_previous_week
import evoshift.composeapp.generated.resources.posture_this_week
import org.jetbrains.compose.resources.stringResource

/*
 * SH-102: the roster builder's controls on a foldable (iPhone Duo or a Pixel Fold like a laptop:
 * below the hinge; flat and nearly square: the right-hand pane): the week pager, the month's
 * main actions and the chosen day's shifts, in reach and off the hinge.
 */

/** Test tag of the controls' week pager. */
const val ROSTER_WEEK_PAGER_TAG = "roster_week_pager"

/** Days in a pager step. */
const val DaysPerWeek = 7

/**
 * The first day (0-based) to show after a week step of [step] weeks from [first] in a month of
 * [days] days: a week on, a week back, never past either end.
 */
fun weekStepTarget(first: Int, step: Int, days: Int): Int {
    if (days <= 0) return 0
    return (first + step * DaysPerWeek).coerceIn(0, days - 1)
}

/**
 * The controls pane: the week pager, then [actions] (publish, auto-assign), then [day] (the
 * chosen day's shifts) or a hint to choose one.
 */
@Composable
internal fun RosterPostureControls(
    onPreviousWeek: () -> Unit,
    onThisWeek: () -> Unit,
    onNextWeek: () -> Unit,
    onPublish: (() -> Unit)?,
    publishEnabled: Boolean,
    publishLabel: String,
    onAutoAssign: (() -> Unit)?,
    autoAssignEnabled: Boolean,
    autoAssignLabel: String,
    day: (@Composable ColumnScope.() -> Unit)?,
    modifier: Modifier = Modifier
) {
    Column(modifier.fillMaxSize()) {
        Row(
            Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 8.dp).testTag(ROSTER_WEEK_PAGER_TAG),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            IconButtonWithTooltip(onClick = onPreviousWeek, modifier = Modifier.handCursor(), tooltip = stringResource(Res.string.posture_previous_week)) {
                Icon(Icons.AutoMirrored.Filled.KeyboardArrowLeft, contentDescription = stringResource(Res.string.posture_previous_week), tint = DeepNavy)
            }
            TextButton(onClick = onThisWeek, modifier = Modifier.handCursor()) {
                Text(stringResource(Res.string.posture_this_week), color = DeepNavy, style = MaterialTheme.typography.titleSmall)
            }
            IconButtonWithTooltip(onClick = onNextWeek, modifier = Modifier.handCursor(), tooltip = stringResource(Res.string.posture_next_week)) {
                Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = stringResource(Res.string.posture_next_week), tint = DeepNavy)
            }
        }
        if (onPublish != null || onAutoAssign != null) {
            Row(
                Modifier.fillMaxWidth().padding(horizontal = 16.dp).padding(bottom = 8.dp),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (onAutoAssign != null) {
                    OutlinedButton(onClick = onAutoAssign, enabled = autoAssignEnabled, modifier = Modifier.weight(1f).handCursor()) {
                        Icon(Icons.Default.AutoFixHigh, contentDescription = null, tint = DeepNavy)
                        Text(autoAssignLabel, color = DeepNavy, modifier = Modifier.padding(start = 8.dp))
                    }
                }
                if (onPublish != null) {
                    GoldButton(text = publishLabel, onClick = onPublish, enabled = publishEnabled, modifier = Modifier.weight(1f))
                }
            }
        }
        HorizontalDivider(color = DeepNavy.copy(alpha = 0.1f))
        if (day != null) {
            Column(Modifier.fillMaxWidth().weight(1f), content = day)
        } else {
            Box(Modifier.fillMaxWidth().weight(1f).background(DeepNavy.copy(alpha = 0.02f)).padding(24.dp), contentAlignment = Alignment.Center) {
                Text(
                    stringResource(Res.string.posture_pick_day),
                    color = DeepNavy.copy(alpha = 0.6f),
                    style = MaterialTheme.typography.bodyMedium,
                    textAlign = TextAlign.Center
                )
            }
        }
    }
}
